import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Stimulus controller for AI logo generation with multiple variants
// Handles form submission via AJAX and real-time progress updates via ActionCable
// Also supports deriving variants from primary via image processing
export default class extends Controller {
  static targets = [
    "basePrompt", "provider", "style",
    "variantCard", "variantPreview", "variantImage", "variantEmpty",
    "variantButton", "variantSpinner", "variantIcon", "variantText",
    "variantDeriveButton", "variantDeriveIcon", "variantDeriveText",
    "deriveAllButton", "deriveAllSpinner", "deriveAllIcon", "deriveAllText"
  ]

  static values = {
    websiteId: String,
    generateUrl: String,
    deriveUrl: String
  }

  connect() {
    this.subscribeToChannel()
    this.generatingVariants = new Set()
    this.derivingVariants = new Set()
    this.derivingAll = false
  }

  disconnect() {
    this.unsubscribeFromChannel()
  }

  subscribeToChannel() {
    if (!this.websiteIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: "WebsiteSettingsChannel", website_id: this.websiteIdValue },
      {
        connected: () => {
          console.log("[LogoGenerator] Connected to WebsiteSettingsChannel")
        },
        disconnected: () => {
          console.log("[LogoGenerator] Disconnected from WebsiteSettingsChannel")
        },
        received: (data) => {
          this.handleMessage(data)
        }
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  // Generate a specific variant
  generateVariant(event) {
    event.preventDefault()

    const button = event.currentTarget
    const variant = button.dataset.variant

    // Validate prompt
    const prompt = this.basePromptTarget.value.trim()
    if (!prompt) {
      this.showToast("Please describe your logo first", "error")
      return
    }

    // Get settings
    const provider = this.providerTarget.value
    const style = this.styleTarget.value

    // Build form data
    const formData = new FormData()
    formData.append("prompt", prompt)
    formData.append("provider", provider)
    formData.append("style", style)
    formData.append("variant", variant)
    formData.append("remove_background", "true")

    // Show loading state
    this.showVariantGenerating(variant)

    // Get the generate URL from the form action or build it
    const generateUrl = `/admin/websites/${this.websiteIdValue}/editor/generate_logo`

    // Submit via fetch
    fetch(generateUrl, {
      method: "POST",
      body: formData,
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log(`[LogoGenerator] ${variant} generation started, waiting for ActionCable...`)
        } else {
          this.showVariantError(variant, data.error || "Failed to start generation")
        }
      })
      .catch((error) => {
        console.error("[LogoGenerator] Fetch error:", error)
        this.showVariantError(variant, "Network error. Please try again.")
      })
  }

  // Derive a specific variant from primary using image processing
  deriveVariant(event) {
    event.preventDefault()

    const button = event.currentTarget
    const variant = button.dataset.variant

    // Build form data
    const formData = new FormData()
    formData.append("variant", variant)

    // Show loading state
    this.showVariantDeriving(variant)

    // Get the derive URL
    const deriveUrl = `/admin/websites/${this.websiteIdValue}/editor/derive_logo`

    // Submit via fetch
    fetch(deriveUrl, {
      method: "POST",
      body: formData,
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log(`[LogoGenerator] ${variant} derivation started, waiting for ActionCable...`)
        } else {
          this.showVariantDeriveError(variant, data.error || "Failed to start derivation")
        }
      })
      .catch((error) => {
        console.error("[LogoGenerator] Fetch error:", error)
        this.showVariantDeriveError(variant, "Network error. Please try again.")
      })
  }

  // Derive all missing variants at once
  deriveAll(event) {
    event.preventDefault()

    // Show loading state on the button
    this.showDeriveAllLoading()

    // Build form data
    const formData = new FormData()
    formData.append("all", "true")

    // Get the derive URL
    const deriveUrl = `/admin/websites/${this.websiteIdValue}/editor/derive_logo`

    // Submit via fetch
    fetch(deriveUrl, {
      method: "POST",
      body: formData,
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log("[LogoGenerator] Batch derivation started, waiting for ActionCable...")
          this.derivingAll = true
        } else {
          this.showDeriveAllError(data.error || "Failed to start derivation")
        }
      })
      .catch((error) => {
        console.error("[LogoGenerator] Fetch error:", error)
        this.showDeriveAllError("Network error. Please try again.")
      })
  }

  handleMessage(data) {
    console.log("[LogoGenerator] Received:", data)
    const variant = data.variant || 'primary'

    switch (data.event) {
      // Generation events
      case "logo_generation_started":
        // Already showing loading state from generateVariant
        break
      case "logo_generation_progress":
        this.updateVariantProgress(variant, data.message)
        break
      case "logo_generation_completed":
        this.showVariantCompleted(variant, data.image_url)
        break
      case "logo_generation_failed":
        this.showVariantError(variant, data.error)
        break

      // Derivation events
      case "logo_derivation_started":
        // Already showing loading state from deriveVariant
        break
      case "logo_derivation_progress":
        this.updateVariantProgress(variant, data.message)
        break
      case "logo_derivation_completed":
        this.showVariantDerived(variant, data.image_url)
        break
      case "logo_derivation_failed":
        this.showVariantDeriveError(variant, data.error)
        break

      // Batch derivation events
      case "logo_batch_derivation_started":
        this.showDeriveAllStarted(data.variants)
        break
      case "logo_variant_derived":
        this.showVariantDerived(variant, data.image_url)
        break
      case "logo_batch_derivation_completed":
        this.showDeriveAllCompleted(data.successful, data.failed)
        break

      // Auto-derivation triggered after primary generation
      case "logo_auto_derivation_triggered":
        this.showToast("Auto-deriving remaining variants...", "info")
        break
    }
  }

  // Find elements for a specific variant
  getVariantElements(variant) {
    const card = this.variantCardTargets.find((c) => c.dataset.variant === variant)
    if (!card) return null

    return {
      card,
      preview: card.querySelector('[data-logo-generator-target="variantPreview"]'),
      image: card.querySelector('[data-logo-generator-target="variantImage"]'),
      empty: card.querySelector('[data-logo-generator-target="variantEmpty"]'),
      button: card.querySelector('[data-logo-generator-target="variantButton"]'),
      spinner: card.querySelector('[data-logo-generator-target="variantSpinner"]'),
      icon: card.querySelector('[data-logo-generator-target="variantIcon"]'),
      text: card.querySelector('[data-logo-generator-target="variantText"]'),
      // Derive-specific elements
      deriveButton: card.querySelector('[data-logo-generator-target="variantDeriveButton"]'),
      deriveIcon: card.querySelector('[data-logo-generator-target="variantDeriveIcon"]'),
      deriveText: card.querySelector('[data-logo-generator-target="variantDeriveText"]')
    }
  }

  showVariantGenerating(variant) {
    this.generatingVariants.add(variant)
    const els = this.getVariantElements(variant)
    if (!els) return

    if (els.button) els.button.disabled = true
    if (els.text) els.text.textContent = "Generating..."
    if (els.spinner) els.spinner.classList.remove("hidden")
    if (els.icon) els.icon.classList.add("hidden")
    if (els.preview) els.preview.classList.add("logo-generating")
  }

  updateVariantProgress(variant, message) {
    const els = this.getVariantElements(variant)
    if (!els) return

    if (els.text) els.text.textContent = message || "Processing..."
  }

  showVariantCompleted(variant, imageUrl) {
    this.generatingVariants.delete(variant)
    const els = this.getVariantElements(variant)
    if (!els) return

    // Reset button
    if (els.button) els.button.disabled = false
    if (els.text) els.text.textContent = "Regenerate"
    if (els.spinner) els.spinner.classList.add("hidden")
    if (els.icon) els.icon.classList.remove("hidden")
    if (els.preview) els.preview.classList.remove("logo-generating")

    // Update image
    if (imageUrl && els.preview) {
      // Hide empty state
      if (els.empty) els.empty.classList.add("hidden")

      // Show/update image
      if (els.image) {
        els.image.src = imageUrl
        els.image.classList.remove("hidden")
      } else {
        // Create image element
        const img = document.createElement("img")
        img.src = imageUrl
        img.alt = `${variant} logo`
        img.className = "max-h-full max-w-full object-contain"
        img.setAttribute("data-logo-generator-target", "variantImage")
        els.preview.appendChild(img)
      }

      // Add/update badge
      const infoSection = els.card.querySelector('.p-3.bg-surface-sunken')
      if (infoSection) {
        let badge = infoSection.querySelector('.badge')
        if (!badge) {
          const headerDiv = infoSection.querySelector('.flex.items-start')
          if (headerDiv) {
            badge = document.createElement("span")
            badge.className = "badge badge-success text-xs"
            badge.textContent = "Done"
            headerDiv.appendChild(badge)
          }
        }
      }
    }

    this.showToast(`${this.variantDisplayName(variant)} logo generated!`, "success")
  }

  showVariantError(variant, error) {
    this.generatingVariants.delete(variant)
    const els = this.getVariantElements(variant)
    if (!els) return

    // Reset button
    if (els.button) els.button.disabled = false
    if (els.text) els.text.textContent = els.image ? "Regenerate" : "Generate"
    if (els.spinner) els.spinner.classList.add("hidden")
    if (els.icon) els.icon.classList.remove("hidden")
    if (els.preview) els.preview.classList.remove("logo-generating")

    this.showToast(error || "Generation failed", "error")
  }

  // ============================================
  // Derivation State Methods
  // ============================================

  showVariantDeriving(variant) {
    this.derivingVariants.add(variant)
    const els = this.getVariantElements(variant)
    if (!els) return

    if (els.deriveButton) els.deriveButton.disabled = true
    if (els.deriveText) els.deriveText.textContent = "Deriving..."
    if (els.spinner) els.spinner.classList.remove("hidden")
    if (els.deriveIcon) els.deriveIcon.classList.add("hidden")
    if (els.preview) els.preview.classList.add("logo-generating")
  }

  showVariantDerived(variant, imageUrl) {
    this.derivingVariants.delete(variant)
    const els = this.getVariantElements(variant)
    if (!els) return

    // Reset derive button
    if (els.deriveButton) els.deriveButton.disabled = false
    if (els.deriveText) els.deriveText.textContent = "Re-derive"
    if (els.spinner) els.spinner.classList.add("hidden")
    if (els.deriveIcon) els.deriveIcon.classList.remove("hidden")
    if (els.preview) els.preview.classList.remove("logo-generating")

    // Update image
    if (imageUrl && els.preview) {
      // Hide empty state
      if (els.empty) els.empty.classList.add("hidden")

      // Show/update image
      if (els.image) {
        els.image.src = imageUrl
        els.image.classList.remove("hidden")
      } else {
        // Create image element
        const img = document.createElement("img")
        img.src = imageUrl
        img.alt = `${variant} logo`
        img.className = "max-h-full max-w-full object-contain"
        img.setAttribute("data-logo-generator-target", "variantImage")
        els.preview.appendChild(img)
      }

      // Add/update badge to "Derived"
      const infoSection = els.card.querySelector('.p-3.bg-surface-sunken')
      if (infoSection) {
        let badge = infoSection.querySelector('.badge')
        if (badge) {
          badge.className = "badge badge-info text-xs"
          badge.textContent = "Derived"
          badge.title = "Derived from primary"
        } else {
          const headerDiv = infoSection.querySelector('.flex.items-start')
          if (headerDiv) {
            badge = document.createElement("span")
            badge.className = "badge badge-info text-xs"
            badge.textContent = "Derived"
            badge.title = "Derived from primary"
            headerDiv.appendChild(badge)
          }
        }
      }
    }

    this.showToast(`${this.variantDisplayName(variant)} variant derived!`, "success")
  }

  showVariantDeriveError(variant, error) {
    this.derivingVariants.delete(variant)
    const els = this.getVariantElements(variant)
    if (!els) return

    // Reset derive button
    if (els.deriveButton) els.deriveButton.disabled = false
    if (els.deriveText) els.deriveText.textContent = els.image ? "Re-derive" : "Derive"
    if (els.spinner) els.spinner.classList.add("hidden")
    if (els.deriveIcon) els.deriveIcon.classList.remove("hidden")
    if (els.preview) els.preview.classList.remove("logo-generating")

    this.showToast(error || "Derivation failed", "error")
  }

  // ============================================
  // Derive All State Methods
  // ============================================

  showDeriveAllLoading() {
    if (this.hasDeriveAllButtonTarget) {
      this.deriveAllButtonTarget.disabled = true
    }
    if (this.hasDeriveAllTextTarget) {
      this.deriveAllTextTarget.textContent = "Deriving..."
    }
    if (this.hasDeriveAllSpinnerTarget) {
      this.deriveAllSpinnerTarget.classList.remove("hidden")
    }
    if (this.hasDeriveAllIconTarget) {
      this.deriveAllIconTarget.classList.add("hidden")
    }
  }

  showDeriveAllStarted(variants) {
    // Already showing loading state
    console.log("[LogoGenerator] Deriving variants:", variants)
  }

  showDeriveAllCompleted(successful, failed) {
    this.derivingAll = false

    // Reset button
    if (this.hasDeriveAllButtonTarget) {
      this.deriveAllButtonTarget.disabled = false
    }
    if (this.hasDeriveAllSpinnerTarget) {
      this.deriveAllSpinnerTarget.classList.add("hidden")
    }
    if (this.hasDeriveAllIconTarget) {
      this.deriveAllIconTarget.classList.remove("hidden")
    }
    if (this.hasDeriveAllTextTarget) {
      // Update count based on remaining
      const remaining = document.querySelectorAll('[data-derivable="true"] .badge.badge-default').length
      this.deriveAllTextTarget.textContent = remaining > 0 ? `Derive All (${remaining})` : "All Derived"
    }

    // Show completion toast
    if (successful && successful.length > 0) {
      const message = `Derived ${successful.length} variant(s)${
        failed && failed.length > 0 ? `, ${failed.length} failed` : ''}`
      this.showToast(message, failed && failed.length > 0 ? "warning" : "success")
    }
  }

  showDeriveAllError(error) {
    this.derivingAll = false

    // Reset button
    if (this.hasDeriveAllButtonTarget) {
      this.deriveAllButtonTarget.disabled = false
    }
    if (this.hasDeriveAllSpinnerTarget) {
      this.deriveAllSpinnerTarget.classList.add("hidden")
    }
    if (this.hasDeriveAllIconTarget) {
      this.deriveAllIconTarget.classList.remove("hidden")
    }
    if (this.hasDeriveAllTextTarget) {
      this.deriveAllTextTarget.textContent = "Derive All"
    }

    this.showToast(error || "Batch derivation failed", "error")
  }

  variantDisplayName(variant) {
    const names = {
      primary: "Primary",
      light: "Light",
      dark: "Dark",
      icon: "Icon",
      horizontal: "Horizontal",
      vertical: "Vertical"
    }
    return names[variant] || variant
  }

  showToast(message, type = "info") {
    // Remove any existing toasts
    document.querySelectorAll(".logo-toast").forEach((t) => t.remove())

    const toast = document.createElement("div")
    toast.className = `logo-toast logo-toast-${type}`

    const iconSvg = type === "success" ?
      `<svg class="logo-toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>` :
      `<svg class="logo-toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`

    toast.innerHTML = `
      <div class="logo-toast-content">
        ${iconSvg}
        <span>${message}</span>
      </div>
    `

    this.ensureToastStyles()
    document.body.appendChild(toast)

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add("logo-toast-show")
    })

    // Auto dismiss
    setTimeout(() => {
      toast.classList.remove("logo-toast-show")
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  ensureToastStyles() {
    if (document.getElementById("logo-generator-styles")) return

    const style = document.createElement("style")
    style.id = "logo-generator-styles"
    style.textContent = `
      .logo-toast {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
        transform: translateY(20px);
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
      }
      .logo-toast-show {
        transform: translateY(0);
        opacity: 1;
      }
      .logo-toast-content {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      }
      .logo-toast-success .logo-toast-content {
        background: #ecfdf5;
        color: #047857;
        border: 1px solid #a7f3d0;
      }
      .logo-toast-error .logo-toast-content {
        background: #fef2f2;
        color: #b91c1c;
        border: 1px solid #fecaca;
      }
      .logo-toast-icon {
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
      }

      /* Loading state for variant preview area */
      .logo-generating {
        position: relative;
      }
      .logo-generating::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(255,255,255,0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: inherit;
      }
      .logo-generating::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 24px;
        height: 24px;
        border: 2px solid #e5e7eb;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: logo-spin 0.8s linear infinite;
        z-index: 1;
      }
      @keyframes logo-spin {
        to { transform: translate(-50%, -50%) rotate(360deg); }
      }
    `
    document.head.appendChild(style)
  }
}
